import {produce} from 'immer'
import type {Document, DumpOptions} from 'js-yaml'
import {CORE_SCHEMA, dump, load, mergeTag, Style, visit} from 'js-yaml'

import type {QueryParams} from '@jetbrains/teamcity-ui/dist/utils'
import {objectToQuery} from '@jetbrains/teamcity-ui/dist/utils'
import omit from 'lodash/omit'

import type {BuildType, GetAllBuildTypesApiArg, GetBuildTypeApiArg} from '../../../../services/rest'
import type {PipelineId} from '../../../../types'
import {stringifyId} from '../../../../types'
import {DEFAULT_NAME as DEFAULT_BRANCH_NAME} from '../../../../utils/branchNames'
import {
  customAgentsNames,
  isCloud,
  jobCountHardLimit,
  jobCountSoftLimit,
} from '../../../../utils/featureToggles'
import type {
  NewIntegrationDto,
  NewVcsRootDto,
  VersionedSettingsDto,
  VcsRootDto,
} from '../services/pipelinesApi.types'
import type {
  Dependency,
  PipelineDependency,
  PipelineDependencyDetails,
  PipelineDependencyId,
  JobRepositorySettings,
  PipelineSettings,
  RunsOn,
} from '../types'

const normalizeJobRepositoryEntry = (
  entry: unknown,
): Record<string, JobRepositorySettings> | null => {
  if (typeof entry === 'string') {
    return {[entry]: {enabled: true}}
  }
  if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
    const [repositoryReference, repositoryValue] = Object.entries(
      entry as Record<string, unknown>,
    )[0] ?? [null, null]
    if (!repositoryReference) {
      return null
    }
    if (repositoryValue == null) {
      return {[repositoryReference]: {enabled: true}}
    }
    if (typeof repositoryValue === 'string') {
      return {[repositoryReference]: {enabled: true, path: repositoryValue}}
    }
    if (typeof repositoryValue === 'object') {
      return {[repositoryReference]: repositoryValue as JobRepositorySettings}
    }
  }
  return null
}

// js-yaml 5 dropped the YAML merge key (<<) from the default load schema
const loadSchema = CORE_SCHEMA.withTags(mergeTag)

export const parsePipeline = (yaml: string): PipelineSettings | undefined => {
  // js-yaml 5 throws on empty input where v4 returned undefined
  if (yaml.trim() === '') {
    return undefined
  }
  const parsedPipeline = load(yaml, {schema: loadSchema}) as PipelineSettings

  // Strip legacy field: existing YAML files may still contain 'import-parameters',
  // but the feature has been removed. Without this, the field would be preserved
  // in the draft and re-serialized back into the YAML on save.
  if (parsedPipeline != null) {
    delete (parsedPipeline as any)['import-parameters']
  }

  if (!parsedPipeline?.jobs) {
    return parsedPipeline
  }

  // TW-95254 Custom deserialization for integrations
  for (const [_, job] of Object.entries(parsedPipeline.jobs)) {
    if (job?.integrations && job.integrations.length > 0) {
      job.integrations = job.integrations
        .map(integration => Object.entries(integration)[0])
        .filter(integration => integration.length === 2)
        .map(integration => ({
          id: integration[1],
          type: integration[0],
        }))
    }
    if (job?.repositories && job.repositories.length > 0) {
      job.repositories = job.repositories
        .map(repository => normalizeJobRepositoryEntry(repository))
        .filter(
          (repository): repository is Record<string, JobRepositorySettings> => repository != null,
        )
    }
  }

  return parsedPipeline
}

export const getVcsRootReference = (vcsRoot?: NewVcsRootDto | VcsRootDto | null) => {
  if (!vcsRoot) {
    return undefined
  }
  if (vcsRoot.isExternal) {
    return vcsRoot.externalVcsRootId ?? vcsRoot.id
  }
  return vcsRoot.id
}

type YamlReplacer = (key: string, value: any) => any

const applyReplacer = (key: string, value: any, replacer: YamlReplacer): any => {
  const replaced = replacer(key, value)
  if (Array.isArray(replaced)) {
    return replaced.map((item, index) => applyReplacer(String(index), item, replacer))
  }
  if (replaced != null && typeof replaced === 'object') {
    return Object.fromEntries(
      Object.entries(replaced).map(([itemKey, itemValue]) => [
        itemKey,
        applyReplacer(itemKey, itemValue, replacer),
      ]),
    )
  }
  return replaced
}

const isYamlWhitespace = (char: string) => char === ' ' || char === '\t'

// Matches the lineWidth at which dump() used to fold long values
const LITERAL_BLOCK_MIN_LENGTH = 80

// Present long one-line values as literal blocks (|-) so they need no quoting
const presentLongStringsAsLiteralBlocks = (documents: Document[]) =>
  visit(documents, (node, ctx) => {
    if (
      !ctx.isKey &&
      node.kind === 'scalar' &&
      node.tag === 'tag:yaml.org,2002:str' &&
      typeof node.value === 'string' &&
      node.value.length > LITERAL_BLOCK_MIN_LENGTH &&
      !/[\n\r]/.test(node.value) &&
      !isYamlWhitespace(node.value[0]) &&
      !isYamlWhitespace(node.value[node.value.length - 1])
    ) {
      node.style ??= new Style()
      node.style.literal = true
    }
  })

// js-yaml 5 removed the dump() replacer option, so it is reimplemented here
// as a JSON.stringify-style pre-walk of the data
export const stringifyPipeline = (
  pipeline: PipelineSettings,
  {replacer, ...opts}: DumpOptions & {replacer?: YamlReplacer} = {},
): string =>
  dump(replacer != null ? applyReplacer('', pipeline, replacer) : pipeline, {
    lineWidth: -1,
    transform: presentLongStringsAsLiteralBlocks,
    ...opts,
  })

export const getDependencyName = (dependency: Dependency) =>
  typeof dependency === 'string' ? dependency : Object.keys(dependency)[0]

export const getPipelinesSettingsWithoutDeprecatedFields = (settings: PipelineSettings) =>
  omit(settings, ['name'])

export const getPreformattingSettings = (key: string, value: any) => {
  const isJobsSection = key === 'jobs'
  const isValueString = typeof value === 'string'
  const isIntegrationsSection = key === 'integrations'

  if (isValueString) {
    return value.trim()
  }

  if (isJobsSection && typeof value === 'object') {
    return Object.keys(value).reduce((accumulator, jobName) => {
      const jobValue = value[jobName]
      const jobNameFormatted = jobName.trim()

      return {
        ...accumulator,
        [jobNameFormatted]: jobValue,
      }
    }, {})
  }

  if (isIntegrationsSection && Array.isArray(value) && value.length > 0) {
    return value
      .filter(integration => integration.type && integration.id)
      .map(integration => ({
        [`${integration.type}`]: integration.id,
      }))
  }

  return value
}

export const getPreformattingIntegrations = (draft: NewIntegrationDto[]) => {
  draft?.forEach(({parameters}) => {
    for (const [key, value] of Object.entries(parameters)) {
      if (value == null) {
        delete parameters[key]
      }
    }
  })
}

const getPreformattingVcsRoot = (draft?: NewVcsRootDto) => {
  if (!draft) {
    return
  }

  if (draft.isExternal) {
    draft.externalVcsRootId = draft.id
  }
  if (draft.isNew && draft.externalVcsRootId == null && draft.id) {
    draft.externalVcsRootId = draft.id
  }

  draft.statusPublishingEnabled = draft.isStatusPublishingEnabled
  draft.pullRequestsTrackingEnabled = draft.trackPullRequests
}

const getPreformattingVcsRoots = (draft?: NewVcsRootDto[]) => {
  draft?.forEach(getPreformattingVcsRoot)
}

export function preformatPipelineVcsData<T extends NewVcsRootDto>(data: {
  vcsRoot?: T | null
  additionalVcsRoots: T[]
}): {vcsRoot?: T | null; additionalVcsRoots: T[]} {
  return {
    ...(data.vcsRoot != null ? {vcsRoot: produce(data.vcsRoot, getPreformattingVcsRoot)} : {}),
    additionalVcsRoots: produce(data.additionalVcsRoots, getPreformattingVcsRoots),
  }
}

export const getCustomAgents = (): Array<{name: string}> => {
  const customAgentsProp = customAgentsNames || null
  return customAgentsProp
    ? customAgentsProp
        .split(',')
        .filter(Boolean)
        .map((i: string) => ({name: i.trim()}))
    : []
}

export const jobLimits = {
  soft: jobCountSoftLimit,
  hard: jobCountHardLimit,
}

export function isSelfHostedRunsOn(runsOn: RunsOn | undefined): boolean {
  if (!isCloud) {
    return true // On-premises TeamCity supports only self-hosted agents, so we can consider any runsOn value as self-hosted
  }

  return typeof runsOn === 'object' ? 'self-hosted' in runsOn : runsOn === 'self-hosted'
}

export const getDependencyDetails = ({
  pipelineId,
  dependencyId,
  buildType,
  branch,
}: {
  pipelineId: PipelineId
  dependencyId: PipelineDependencyId
  buildType: BuildType
  branch: string
}): {
  pipelineId: PipelineId
  branch: string
  dependencyId: PipelineDependencyId
  value: PipelineDependencyDetails
} => {
  const {name, project} = buildType
  const {holdsPipeline: isPipeline, parentProjectId, ancestorProjects} = project ?? {}
  const pipelineParentProjectId = isPipeline
    ? ancestorProjects?.project?.find(({id}) => id === parentProjectId)?.parentProjectId
    : null
  return {
    pipelineId,
    branch,
    dependencyId,
    value: {
      name: isPipeline ? project?.name : name,
      projectId: isPipeline ? parentProjectId : project?.id,
      parentProjectId: pipelineParentProjectId ?? parentProjectId,
      isPipeline,
    },
  }
}

export const getDependencyArgs = (id: PipelineDependencyId): GetBuildTypeApiArg => ({
  btLocator: `id:${stringifyId(id)}`,
  fields:
    'id,name,project(id,name,parentProjectId,archived,holdsPipeline,ancestorProjects(project(id,name,parentProjectId,archived)))',
})

export const getUpstreamDependenciesArgs = (pipelineId: PipelineId): GetAllBuildTypesApiArg => ({
  locator: `snapshotDependency(to:(id:${stringifyId(pipelineId)}))`,
  fields:
    'buildType(id,name,project(id,name,parentProjectId,archived,holdsPipeline,ancestorProjects(project(id,name,parentProjectId,archived))))',
})

export const getPipelineDependencyId = (dependency: PipelineDependency) =>
  typeof dependency === 'string' ? dependency : (Object.keys(dependency)[0] as PipelineDependencyId)

export const getPipelineDependency = (dependency: PipelineDependency, dependencyId: string) =>
  typeof dependency === 'string'
    ? dependency === dependencyId
    : dependency[dependencyId as PipelineDependencyId]

export function getBuildFeatureFormId(type: string, index: number | string): string {
  return `${type}-${index}`
}

export const buildEditPipelineSearch = ({branch, ...params}: QueryParams) =>
  objectToQuery({
    ...params,
    branch: branch != null && branch !== DEFAULT_BRANCH_NAME ? branch : undefined,
  })

export const isYamlStoredInRepo = (versionedSettings?: VersionedSettingsDto | null) =>
  versionedSettings?.storedInRepo === true && versionedSettings.format === 'pipelines-yaml'
